import React, { useState, useEffect } from "react";
import CryptoJS from "crypto-js";
import {
  Box,
  FormControl,
  HStack,
  Text,
  VStack,
  useMediaQuery,
} from "native-base";
import { useDispatch } from "react-redux";
import CommonHeading from "../../components/headings";
import { COLORS } from "../../utils/colors";
import NewInputField from "../../components/input/newInput";
import CustomButton from "../../components/button";
import { COMMONFONTSIZE, COMMONFONTWEIGHT } from "../../utils/fonts";
import { emailValidator } from "../../utils/validator";
import { Toaster } from "../../components/toaster";
import { isLoggedIn, setCoordinatorStatus } from "../../store/reducers/userSlice";
import {
  externalLoginAPI,
  getUserInformations,
  verifyOtpAPI,
} from "../../api/apis/login";
import { checkEventCoordinatorAPI } from "../../api/apis/workflow";
import "../loginPageCards/style.css";
import config from "../../utils/config";
import DividerLine from "../../components/divider";
import { MOBILE_VIEW_WIDTH } from "../../utils/screens";
import { SSO_LOGIN_URL } from "../../api/url";
import { LoaderAnimation } from "../../components/spinner";
import { generateCMSRedirectURL } from "../../utils/cmsRedirect";

const CmsLoginPageCard = ({ width = "100%", py = "13rem", pl = "", pr = "" }) => {
  const dispatch = useDispatch();
  const toaster = Toaster();
  const [loginDetails, setLoginDetails] = useState({
    email: "",
    password: "",
  });
  const [showPasswordPage, setShowPasswordPage] = useState(false);
  const [resendCounter, setResendCounter] = useState(0);
  const [countdown, setCountdown] = useState(0);
  const [isCountingDown, setIsCountingDown] = useState(false);
  const [MobileWindow] = useMediaQuery({
    maxWidth: MOBILE_VIEW_WIDTH,
  });

  // Check if user is already logged in and redirect to CMS portal
  useEffect(() => {
    const checkExistingSession = async () => {
      const accessToken = sessionStorage.getItem("access_token");
      const refreshToken = sessionStorage.getItem("refresh_token");
      const sessionId = sessionStorage.getItem("session_id");
      const content = sessionStorage.getItem("content");

      if (accessToken && refreshToken && sessionId && content) {
        // User might be logged in, verify tokens are valid
        try {
          const info = await getUserInformations();
          if (!info.error && info.code !== 401) {
            // Tokens are valid, redirect to CMS portal
            const cmsURL = generateCMSRedirectURL();
            if (cmsURL) {
              window.location.href = cmsURL;
            }
          } else {
            // Tokens are invalid/expired, clear them and stay on login page
            sessionStorage.clear();
          }
        } catch (error) {
          // Error validating tokens, clear them and stay on login page
          console.error("Error validating tokens:", error);
          sessionStorage.clear();
        }
      } else if (accessToken && refreshToken && sessionId) {
        // User has tokens but no content - this might be from SSO callback
        // Check if this is a CMS login session
        const isCmsLogin = sessionStorage.getItem("cmsLogin");
        if (isCmsLogin === "true") {
          // Get user info and redirect to CMS
          try {
            const info = await getUserInformations();
            if (!info.error && info.code !== 401) {
              sessionStorage.setItem(
                "content",
                CryptoJS.AES.encrypt(
                  JSON.stringify(info?.data?.data?.data),
                  config.userKey
                ).toString()
              );
              const cmsURL = generateCMSRedirectURL();
              if (cmsURL) {
                sessionStorage.removeItem("cmsLogin");
                window.location.href = cmsURL;
              }
            } else {
              // Tokens invalid, clear and stay on login page
              sessionStorage.clear();
            }
          } catch (error) {
            console.error("Error getting user info for CMS redirect:", error);
            sessionStorage.clear();
          }
        }
      }
    };

    checkExistingSession();
  }, []);

  const onInputChange = (e, eName) => {
    let name = eName;
    let value = e.target.value;
    if (name === "password" && (isNaN(value) || value.length > 6)) {
      return;
    }

    let detail = { ...loginDetails };
    detail[name] = value;
    setLoginDetails(detail);
  };

  const onLogin = async () => {
    if (
      loginDetails.email === "" ||
      !emailValidator(loginDetails.email.toLowerCase().trim())
    ) {
      toaster("Enter a valid Mail ID", "error", "error");
      return;
    }

    LoaderAnimation.show();
    let externalEmail = loginDetails.email.toLowerCase().trim();
    let res = await externalLoginAPI(externalEmail);
    if (res.error) {
      toaster(res.message, "error", "error");
      LoaderAnimation.hide();
      return;
    } else {
      toaster("OTP has been sent through Email", "success", "success");
      setShowPasswordPage(true);
    }
    LoaderAnimation.hide();
  };

  const startCountdown = (count) => {
    setCountdown(count);
    setIsCountingDown(true);
  };

  useEffect(() => {
    if (isCountingDown && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0) {
      setIsCountingDown(false);
    }
  }, [countdown, isCountingDown]);

  const onVerify = async () => {
    if (loginDetails.password === "" || loginDetails.password.length !== 6) {
      toaster("Please enter the valid OTP", "error", "error");
      return;
    }

    let externalEmail = loginDetails.email.toLowerCase().trim();
    let externalOTP = loginDetails.password.trim();
    LoaderAnimation.show();
    let res = await verifyOtpAPI(externalEmail, externalOTP);
    if (res.error) {
      toaster(res.message, "error", "error");
      LoaderAnimation.hide();
      return;
    } else {
      sessionStorage.setItem(
        "access_token",
        CryptoJS.AES.encrypt(
          JSON.stringify(res?.data?.data?.data?.access_token),
          config.tokenKey
        ).toString()
      );
      sessionStorage.setItem(
        "refresh_token",
        CryptoJS.AES.encrypt(
          JSON.stringify(res?.data?.data?.data?.refresh_token),
          config.tokenKey
        ).toString()
      );
      sessionStorage.setItem(
        "session_id",
        CryptoJS.AES.encrypt(
          JSON.stringify(res?.data?.data?.data?.session_id),
          config.tokenKey
        ).toString()
      );
      const info = await getUserInformations();
      if (info.error) {
        toaster(info.message, "error", "error");
        LoaderAnimation.hide();
        return;
      } else {
        sessionStorage.setItem(
          "content",
          CryptoJS.AES.encrypt(
            JSON.stringify(info?.data?.data?.data),
            config.userKey
          ).toString()
        );
        dispatch(isLoggedIn(true));
        
        // Check coordinator status
        try {
          // Check user role from getUserInformations response
          const userRole = info?.data?.data?.data?.role;
          if (userRole === 'vendor' || userRole === 'External_employee' || userRole === 'employee') {
            // Skip API call for vendor, external employee, and employee roles
            dispatch(setCoordinatorStatus(false));
          } else {
            // Call API for other roles
            const coordinatorRes = await checkEventCoordinatorAPI();
            if (coordinatorRes?.error) {
              dispatch(setCoordinatorStatus(false));
            } else {
              // If status_code is 200, user is coordinator
              const isCoordinator = coordinatorRes.data?.data?.status_code === 200;
              dispatch(setCoordinatorStatus(isCoordinator));
            }
          }
        } catch (error) {
          dispatch(setCoordinatorStatus(false));
        }
        
        setResendCounter(0);
        toaster("Login successful", "success", "success");
        
        // Redirect to CMS portal after successful login
        const cmsURL = generateCMSRedirectURL();
        if (cmsURL) {
          window.location.href = cmsURL;
        } else {
          toaster("Unable to redirect to CMS portal", "error", "error");
        }
      }
    }
    LoaderAnimation.hide();
  };

  const handleMicrosoftLogin = () => {
    // Store a flag to indicate this is CMS login
    sessionStorage.setItem("cmsLogin", "true");
    const microsoftLoginUrl = config.authUrl + SSO_LOGIN_URL;
    window.location.href = microsoftLoginUrl;
  };

  const handleResendOtp = () => {
    // if (resendCounter < 3) {
    onLogin();
    startCountdown(30);
    // setResendCounter((prevCounter) => prevCounter + 1);
    // } else {
    // onLogin();
    // setShowPasswordPage(false);
    // startCountdown(300);
    // }
  };

  return (
    <VStack space={3} w={width} py={py} pl={pl} pr={pr}>
      <CommonHeading
        name="email"
        text="CMS Login"
        color={COLORS.DETAIL_CARD_TEXT_COLOR}
        fontSize="24px"
        fontWeight={COMMONFONTWEIGHT.LARGE_FONT_WEIGHT}
      />
      <Box alignItems={"center"}>
        <Box width="100%" textAlign="left">
          <FormControl.Label>For ABG Employees</FormControl.Label>
        </Box>
        <button
          className="microsoft-signin-button"
          onClick={handleMicrosoftLogin}
        >
          <img
            src="https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg"
            alt="Microsoft logo"
            className="microsoft-logo"
          />
          <p>Sign in with Microsoft</p>
        </button>
      </Box>
      <HStack
        alignItems={"center"}
        my={MobileWindow ? 3 : 5}
        justifyContent={"center"}
      >
        <Box width={"23%"}>
          <DividerLine direction="horizontal" />
        </Box>
        <Text
          fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
          color={COLORS.PACKAGE_DETAILS_TEXT_COLOR}
          px={5}
        >
          OR
        </Text>
        <Box width={"23%"}>
          <DividerLine direction="horizontal" />
        </Box>
      </HStack>
      {!showPasswordPage && (
        <div className="multi-colored-placeholder-container">
          <NewInputField
            name="email"
            value={loginDetails.email}
            onChange={onInputChange}
            placeholder=""
            label="For Non ABG Employees"
            className="multi-colored-input"
          />
          {loginDetails.email === "" && (
            <div className="multi-colored-placeholder-container__text">
              <span className="multi-colored-placeholder-container__text--first">
                Enter Email ID
              </span>
              <span className="multi-colored-placeholder-container__text--second">
                {" "}
                /Mobile No
              </span>
            </div>
          )}
        </div>
      )}
      {showPasswordPage && (
        <>
          <NewInputField
            name="password"
            value={loginDetails.password}
            onChange={onInputChange}
            type="otp"
            placeholder="Enter OTP"
          />
          <Box mt={2} alignItems={"flex-end"}>
            {isCountingDown ? (
              <Text
                fontWeight={COMMONFONTWEIGHT.LARGE_FONT_WEIGHT}
                fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                color={COLORS.THEME_COLOR}
                style={{ cursor: "pointer" }}
              >
                Resend OTP in {countdown}s
              </Text>
            ) : (
              <Text
                onPress={handleResendOtp}
                fontWeight={COMMONFONTWEIGHT.LARGE_FONT_WEIGHT}
                fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                color={COLORS.THEME_COLOR}
                style={{ cursor: "pointer" }}
              >
                Resend OTP
              </Text>
            )}
          </Box>
        </>
      )}
      {!showPasswordPage && (
        <Box mt={3}>
          <CustomButton onPress={onLogin} text="Login" />
        </Box>
      )}
      {showPasswordPage && (
        <Box mt={3}>
          <CustomButton onPress={onVerify} text="Verify" />
        </Box>
      )}
    </VStack>
  );
};

export default CmsLoginPageCard;
